import { useNavigate } from "react-router-dom";
import Header from "../.shared/header/header";
import Footer from "../.shared/footer/footer";
import ProductCardV22 from "./ProductCardV22";
import PlanCarousel from "./PlanCarousel";
import styles from "./ProgramsV22.module.css";

const ROWS = [
    {
        frequency: 'threeMonths',
        title: '3-Month Program',
        pillLabel: 'Best value · Save up to 15%',
        savePill: true,
        cards: [
            { medication: 'Semaglutide', dosageForm: 'injectable', tint: 'rose', isSale: true, membersChoice: true, socialProof: { icon: 'hot', label: 'Chosen by 60+ members this month' }, perks: ['Proven GLP-1 Therapy', 'Weekly Injection'] },
            { medication: 'Semaglutide', dosageForm: 'suspension', tint: 'amber', perks: ['Needle-Free Treatment', 'Daily Sublingual Dosing'] },
            { medication: 'Tirzepatide', dosageForm: 'injectable', tint: 'teal', socialProof: { icon: 'tag', label: 'Best Value' }, perks: ['Dual GIP/GLP-1 Formula', 'Weekly Injection'] },
            { medication: 'Tirzepatide', dosageForm: 'suspension', tint: 'amber', perks: ['Dual GIP/GLP-1 Formula', 'Needle-Free Treatment'] },
        ],
    },
    {
        frequency: 'monthly',
        title: 'Month-To-Month',
        pillLabel: 'Flexible · Cancel anytime',
        highlight: true,
        cards: [
            { medication: 'Semaglutide', dosageForm: 'injectable', tint: 'cyan', socialProof: { icon: 'trend', label: 'Trending Choice' }, perks: ['Proven GLP-1 Therapy', 'Weekly Injection'] },
            { medication: 'Semaglutide', dosageForm: 'suspension', tint: 'amber', perks: ['Needle-Free Treatment', 'Daily Sublingual Dosing'] },
            { medication: 'Tirzepatide', dosageForm: 'injectable', tint: 'teal', socialProof: { icon: 'star', label: 'Trusted by hundreds of members' }, perks: ['Dual GIP/GLP-1 Formula', 'Weekly Injection'] },
            { medication: 'Tirzepatide', dosageForm: 'suspension', tint: 'leaf', perks: ['Dual GIP/GLP-1 Formula', 'Needle-Free Treatment'] },
        ],
    },
];

const TRUST_ITEMS = [
    {
        title: 'Licensed clinicians',
        description: 'Every intake is reviewed by a licensed clinician who decides whether treatment is appropriate.',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l7 4v6c0 5-3.5 9-7 10-3.5-1-7-5-7-10V6l7-4z"></path><polyline points="9 12 11 14 15 10"></polyline></svg>,
    },
    {
        title: 'Free shipping',
        description: 'Discreet delivery on every plan. Injectables ship refrigerated with handling instructions included.',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l5 5v5h-3"></path><path d="M3 7v10h3"></path><circle cx="8" cy="17" r="2"></circle><circle cx="17" cy="17" r="2"></circle></svg>,
    },
    {
        title: 'Ongoing support',
        description: 'Message your care team anytime. Dose adjustments are clinician-guided and included in every plan.',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>,
    },
    {
        title: 'Cancel anytime',
        description: 'No long-term lock-in on monthly plans. 3-month plans can be paused if treatment changes.',
        icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>,
    },
];

export default function ProgramsV22() {
    const navigate = useNavigate();

    return (
        <div className={styles.page}>
            <Header nonSticky />

            <section className={styles.pageTop}>
                <div className={styles.pageTopInner}>
                    <div className={styles.pageTopText}>
                        <h1 className={styles.pageTopTitle}>Clinician-guided weight management, <span className={styles.grad}>tailored to your needs.</span></h1>
                        <p className={styles.pageTopSub}>Four medications, two dosage forms, and a plan that fits your pace.</p>
                    </div>
                </div>
            </section>

            <div className={styles.gridWrap}>
                <div className={styles.rows}>
                    {ROWS.map((row) => (
                        <section key={row.frequency} className={`${styles.row} ${row.highlight ? styles.rowFeatured : ''}`} data-row={row.frequency}>
                            <div className={styles.rowHead}>
                                <h2>{row.title}</h2>
                                <span className={`${styles.rowHeadPill} ${row.savePill ? styles.rowHeadPillSave : ''}`}>{row.pillLabel}</span>
                                <div className={styles.rowHeadSep}></div>
                            </div>
                            <PlanCarousel>
                                {row.cards.map((card) => {
                                    const key = `${row.frequency}-${card.medication}-${card.dosageForm}`;
                                    return (
                                        <ProductCardV22
                                            key={key}
                                            {...card}
                                            frequency={row.frequency}
                                        />
                                    );
                                })}
                            </PlanCarousel>
                        </section>
                    ))}
                </div>
            </div>

            <section className={styles.trust}>
                {TRUST_ITEMS.map((item) => (
                    <div className={styles.trustItem} key={item.title}>
                        <div className={styles.trustIco}>{item.icon}</div>
                        <div>
                            <h4>{item.title}</h4>
                            <p>{item.description}</p>
                        </div>
                    </div>
                ))}
            </section>

            <section className={styles.faqBanner}>
                <h3>Have questions about our programs?</h3>
                <button type="button" className={styles.faqCta} onClick={() => navigate('/knowledge-center')}>View our FAQ section</button>
                <p className={styles.faqTail}>for detailed answers about GLP-1 medications, dosage, side effects, and more.</p>
            </section>

            <div className={styles.legal}>
                <p>All patients undergo a thorough medical evaluation to ensure GLP-1 therapy is safe and appropriate. If you are determined ineligible for a prescription after clinical assessment, you will receive a full refund.</p>
                <p>†Ozempic® and Wegovy® are registered trademarks of Novo Nordisk A/S. Mounjaro® and Zepbound® are registered trademarks of Eli Lilly and Company. These trademark holders are not affiliated with and do not endorse TB2 Health.</p>
                <p>Our telehealth weight loss programs are designed to help you achieve your weight loss goals safely and effectively. Programs are subscription-based, with either monthly or quarterly recurring payments. They include a consultation with a Clinician licensed in your state assisted by a Patient Care Coordinator, whom you can contact with any questions or concerns at any time. Depending on the regulations in your state, this may include a call or video visit. Based on information gathered during your consultation and your choices, the Clinician may prescribe a GLP-1 agonist that is right for you. Your Patient Care Coordinator will help you with any questions you may have about your prescription, including how to use it and how to manage any side effects. If you are determined ineligible for a prescription, you will receive a full refund. New patients typically start with a lower dose and increase over time until they find the dosage that is most effective for them. Patients who have taken GLP-1s before may be able to start at a higher dose. Talk to your clinician about what is best for you. Monthly check-ins and follow-ups are included, along with free overnight shipping to your door. Cancel at any time.</p>
            </div>

            <div className={styles.compliance}>
                <p>*Medication options offered through the program are available only following evaluation and determination by independent licensed clinicians, who exercise independent medical judgment based on each patient's individual health profile. Treatment types and eligibility are determined solely by the clinician as part of a personalized treatment plan. If prescribed, all medications are fulfilled and dispensed exclusively by licensed third-party pharmacies operating in compliance with all applicable state and federal laws and regulatory requirements. We do not manufacture, dispense, distribute, or sell medications.</p>
                <p>* Images shown on this website are for illustrative purposes only and may not represent the exact medication, packaging, labeling, injection device, or appearance you receive. Actual products may vary depending on the licensed pharmacy fulfilling your prescription, the manufacturer, and your personalized treatment plan.</p>
            </div>

            <Footer />
        </div>
    );
}
